import {
  DozenSupabaseClient,
  Tables,
  TablesInsert,
} from "../../database.types";
import { App } from "../types/types";

// League CRUD operations
const leagueService = {
  async getAll(supabase: DozenSupabaseClient): Promise<Tables<"leagues">[]> {
    const { data, error } = await supabase
      .from("leagues")
      .select("*")
      .order("name");

    if (error) {
      console.error("Error fetching leagues:", error);
      throw error;
    }

    return data || [];
  },

  async getById(
    supabase: DozenSupabaseClient,
    id: string,
  ): Promise<Tables<"leagues"> | null> {
    const { data, error } = await supabase
      .from("leagues")
      .select("*")
      .eq("id", id)
      .single();

    if (error) {
      console.error(`Error fetching league with id ${id}:`, error);
      throw error;
    }

    return data;
  },

  /*
   * Fetch all leagues for a specific user - leagues they are a member of and a creator of
   */
  async getAllUserLeagues(
    supabase: DozenSupabaseClient,
    userId: string,
  ): Promise<Tables<"leagues">[]> {
    const [memberResult, creatorResult] = await Promise.all([
      supabase
        .from("league_members")
        .select("*, leagues(*)")
        .eq("user_id", userId),
      supabase
        .from("leagues")
        .select("*")
        .eq("creator_id", userId),
    ]);

    if (memberResult.error) {
      console.error(`Error fetching leagues for user ${userId}:`, memberResult.error);
      throw memberResult.error;
    }
    if (creatorResult.error) {
      console.error(`Error fetching created leagues for user ${userId}:`, creatorResult.error);
      throw creatorResult.error;
    }

    const memberLeagues = (memberResult.data || [])
      .map((row: any) => row.leagues as Tables<"leagues">)
      .filter(Boolean);

    const creatorLeagues = creatorResult.data || [];

    const leagueMap = new Map<string, Tables<"leagues">>();
    for (const league of [...memberLeagues, ...creatorLeagues]) {
      leagueMap.set(league.id, league);
    }

    return Array.from(leagueMap.values())
      .sort((a, b) => a.name.localeCompare(b.name));
  },

  async create(
    supabase: DozenSupabaseClient,
    league: Omit<TablesInsert<"leagues">, "id" | "created_at" | "updated_at">,
  ): Promise<Tables<"leagues">> {
    const { data, error } = await supabase
      .from("leagues")
      .insert(league)
      .select()
      .single();

    if (error) {
      console.error("Error creating league:", error);
      throw error;
    }

    return data;
  },

  async update(
    supabase: DozenSupabaseClient,
    id: string,
    updates: Partial<
      Omit<Tables<"leagues">, "id" | "created_at" | "updated_at">
    >,
  ): Promise<Tables<"leagues">> {
    const { data, error } = await supabase
      .from("leagues")
      .update(updates)
      .eq("id", id)
      .select()
      .single();

    if (error) {
      console.error(`Error updating league with id ${id}:`, error);
      throw error;
    }

    return data;
  },

  async delete(supabase: DozenSupabaseClient, id: string): Promise<void> {
    const { error } = await supabase.from("leagues").delete().eq("id", id);

    if (error) {
      console.error(`Error deleting league with id ${id}:`, error);
      throw error;
    }
  },

  // find league by leagues.join_code, if it exists add new row in league_members for that league and user. Gonna want to refresh leagues after this
  async joinLeagueViaCode(
    supabase: DozenSupabaseClient,
    userId: string,
    joinCode: string,
  ): Promise<void> {
    const { data, error } = await supabase
      .from("leagues")
      .select("*")
      .eq("join_code", joinCode);

    if (error) {
      throw error;
    }

    const matchedLeague = data?.[0] as Tables<"leagues">;

    if (matchedLeague) {
      await supabase.from("league_members").insert({
        league_id: matchedLeague.id,
        user_id: userId,
      });
    } else {
      throw new Error("Unable to find league with matching join code");
    }
  },

  async getLeagueMembers(
    supabase: DozenSupabaseClient,
    leagueId: string,
  ): Promise<App.LeagueMemberWithProfile[]> {
    const { data, error } = await supabase
      .from("league_members")
      .select("*, profiles!league_members_user_id_fkey1(*)")
      .eq("league_id", leagueId);

    if (error) {
      console.error(`Error fetching members for league ${leagueId}:`, error);
      throw error;
    }

    return (data || []).map(({ profiles, ...member }) => ({
      ...member,
      profile: profiles,
    }));
  },

  async updateMemberTeam(
    supabase: DozenSupabaseClient,
    leagueMemberId: string,
    teamId: string | null,
  ): Promise<void> {
    const { error } = await supabase
      .from("league_members")
      .update({ team_id: teamId })
      .eq("id", leagueMemberId);

    if (error) {
      console.error(
        `Error updating team for league member ${leagueMemberId}:`,
        error,
      );
      throw error;
    }
  },
};

export default leagueService;
